import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components';
import { Login, Dashboard, Patients, Appointments, MedicalRecords, Financial } from '../pages';
import CreatePatient from '../pages/Patients/Create';
import EditPatient from '../pages/Patients/Edit';
import CompanyManagement from '../pages/CompanyManagement';
import UserManagement from '../pages/UserManagement';
import Profile from '../pages/Profile';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border" role="status">
          <span className="sr-only">Carregando...</span>
        </div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated, user } = useAuth();

  // Redirect based on role after login
  const getDefaultRoute = () => {
    if (!user) return '/dashboard';
    return user.role === 'admin' ? '/admin/companies' : '/dashboard';
  };

  return (
    <Routes>
      <Route 
        path="/login" 
        element={isAuthenticated ? <Navigate to={getDefaultRoute()} replace /> : <Login />} 
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to={getDefaultRoute()} replace />} />
        
        {/* Operational routes - accessible by managers and users */}
        <Route 
          path="dashboard" 
          element={user?.role === 'admin' ? <Navigate to="/admin/companies" replace /> : <Dashboard />} 
        />
        <Route 
          path="patients" 
          element={user?.role === 'admin' ? <Navigate to="/admin/companies" replace /> : <Patients />} 
        />
        <Route 
          path="patients/create" 
          element={user?.role === 'admin' ? <Navigate to="/admin/companies" replace /> : <CreatePatient />} 
        />
        <Route 
          path="patients/edit/:id" 
          element={user?.role === 'admin' ? <Navigate to="/admin/companies" replace /> : <EditPatient />} 
        />
        <Route 
          path="appointments" 
          element={user?.role === 'admin' ? <Navigate to="/admin/companies" replace /> : <Appointments />} 
        />
        <Route 
          path="medical-records" 
          element={user?.role === 'admin' ? <Navigate to="/admin/companies" replace /> : <MedicalRecords />} 
        />
        <Route 
          path="financial" 
          element={user?.role === 'admin' ? <Navigate to="/admin/companies" replace /> : <Financial />} 
        />
        
        {/* Profile route - accessible by all authenticated users */}
        <Route path="profile" element={<Profile />} />
        
        {/* Admin routes - accessible only by admins */}
        <Route 
          path="admin/companies" 
          element={user?.role === 'admin' ? <CompanyManagement /> : <Navigate to="/dashboard" replace />} 
        />
        <Route 
          path="admin/users" 
          element={user?.role === 'admin' || user?.role === 'manager' ? <UserManagement /> : <Navigate to="/dashboard" replace />} 
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AppRoutes;
